/**
 * StripeSettings.tsx
 * Stripe billing configuration page — connect a Stripe account, set company info,
 * default currency, and test the connection.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface StripeConfig {
  hasKey: boolean;
  publishableKey: string;
  companyName: string;
  companyEmail: string;
  defaultCurrency: string;
}

const CURRENCIES = [
  { value: "usd", label: "USD — US Dollar" },
  { value: "eur", label: "EUR — Euro" },
  { value: "gbp", label: "GBP — British Pound" },
  { value: "cad", label: "CAD — Canadian Dollar" },
  { value: "aud", label: "AUD — Australian Dollar" },
];

export default function StripeSettings() {
  const api = useApi();
  const toastAlert = useToastAlert();

  const [config, setConfig] = useState<StripeConfig>({
    hasKey: false,
    publishableKey: "",
    companyName: "",
    companyEmail: "",
    defaultCurrency: "usd",
  });
  const [loading, setLoading] = useState(true);

  const [secretKey, setSecretKey] = useState("");
  const [publishableKey, setPublishableKey] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [defaultCurrency, setDefaultCurrency] = useState("usd");

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState<"idle" | "success" | "error">("idle");
  const [testMessage, setTestMessage] = useState("");

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await api.admin.get("api/StripeConfig");
      if (resp.ok) {
        const data: StripeConfig = await resp.json();
        setConfig(data);
        setPublishableKey(data.publishableKey ?? "");
        setCompanyName(data.companyName ?? "");
        setCompanyEmail(data.companyEmail ?? "");
        setDefaultCurrency(data.defaultCurrency ?? "usd");
      }
    } catch {
      // non-critical — page still usable for initial setup
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  async function handleSave() {
    setSaving(true);
    setTestStatus("idle");
    try {
      const body: Record<string, string> = {
        publishableKey,
        companyName,
        companyEmail,
        defaultCurrency,
      };
      if (secretKey) {
        body.secretKey = secretKey;
      }
      const resp = await api.admin.post("api/StripeConfig", body);
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Stripe settings saved.");
        setSecretKey("");
        await fetchConfig();
      } else {
        const err = await resp.json().catch(() => ({ message: "Save failed." }));
        toastAlert.displayToast(Severity.Error, err.message || "Save failed.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }

  async function handleTestConnection() {
    setTesting(true);
    setTestStatus("idle");
    try {
      const resp = await api.admin.post("api/StripeConfig/test", {});
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setTestStatus("success");
        setTestMessage((data as Record<string, string>).message || "Connection successful.");
      } else {
        const data = await resp.json().catch(() => ({}));
        setTestStatus("error");
        setTestMessage((data as Record<string, string>).message || "Connection test failed.");
      }
    } catch {
      setTestStatus("error");
      setTestMessage("Could not reach Stripe. Check your secret key.");
    } finally {
      setTesting(false);
    }
  }

  const labelClass = "block text-sm font-medium text-slate-700 mb-1";
  const inputClass =
    "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-500 bg-white";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="fa-solid fa-spinner fa-spin text-slate-400 text-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Stripe Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Connect your Stripe account to send invoices, manage templates, and collect payments.
        </p>
      </div>

      {config.hasKey && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
          <i className="fa-solid fa-circle-check text-emerald-500" />
          <div>
            <p className="text-sm font-medium text-emerald-800">Connected to Stripe</p>
            {config.companyName && (
              <p className="text-xs text-emerald-600">
                {config.companyName}
                {config.publishableKey
                  ? ` · pk_···${config.publishableKey.slice(-4)}`
                  : ""}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <div>
          <label className={labelClass}>Secret Key</label>
          {config.hasKey && !secretKey ? (
            <div className="flex items-center gap-3">
              <div
                className={`${inputClass} text-slate-400 flex-1`}
                style={{ letterSpacing: "0.1em" }}
              >
                ●●●●●●●● saved
              </div>
              <button
                className="text-sm text-cornflower-blue-600 hover:underline whitespace-nowrap"
                onClick={() => setSecretKey(" ")}
              >
                Replace
              </button>
            </div>
          ) : (
            <input
              type="password"
              className={inputClass}
              placeholder="sk_live_..."
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              autoComplete="new-password"
            />
          )}
          <p className="text-xs text-slate-400 mt-1">
            Find this in the Stripe Dashboard under Developers &rarr; API Keys.
          </p>
        </div>

        <div>
          <label className={labelClass}>Publishable Key</label>
          <input
            type="text"
            className={inputClass}
            placeholder="pk_live_..."
            value={publishableKey}
            onChange={(e) => setPublishableKey(e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass}>Company Name</label>
          <input
            type="text"
            className={inputClass}
            placeholder="Acme Corp"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
          <p className="text-xs text-slate-400 mt-1">Appears on invoices sent to your customers.</p>
        </div>

        <div>
          <label className={labelClass}>Company Email</label>
          <input
            type="email"
            className={inputClass}
            placeholder="billing@yourcompany.com"
            value={companyEmail}
            onChange={(e) => setCompanyEmail(e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass}>Default Currency</label>
          <select
            className={inputClass}
            value={defaultCurrency}
            onChange={(e) => setDefaultCurrency(e.target.value)}
          >
            {CURRENCIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {testStatus !== "idle" && (
        <div
          className={`flex items-center gap-3 rounded-lg px-4 py-3 border ${
            testStatus === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          <i
            className={`fa-solid ${
              testStatus === "success"
                ? "fa-circle-check text-emerald-500"
                : "fa-circle-xmark text-red-500"
            }`}
          />
          <span className="text-sm">{testMessage}</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-cornflower-blue-600 text-white text-sm font-medium hover:bg-cornflower-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving && <i className="fa-solid fa-spinner fa-spin" />}
          {saving ? "Saving…" : "Save Settings"}
        </button>

        {config.hasKey && (
          <button
            onClick={handleTestConnection}
            disabled={testing}
            className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
          >
            {testing && <i className="fa-solid fa-spinner fa-spin" />}
            {testing ? "Testing…" : "Test Connection"}
          </button>
        )}
      </div>
    </div>
  );
}
